import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { PhoneInput } from '../ui/phone-input';
import { Loader2, Building2 } from 'lucide-react';
import { settingsApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const emptyCompanySettings = {
  company_name: '',
  legal_name: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  logo_url: '',
};

const CompanySettings: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(emptyCompanySettings);

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: () => settingsApi.system.getAll(),
  });

  // Update form data when settings are loaded
  useEffect(() => {
    if (settings && settings.length > 0) {
      const companyData = settings.find((s: any) => s.category === 'company');
      if (companyData && companyData.setting_value) {
        const parsedValue = typeof companyData.setting_value === 'string' 
          ? JSON.parse(companyData.setting_value) 
          : companyData.setting_value;
        setFormData({
          ...emptyCompanySettings,
          ...parsedValue,
          phone: parsedValue?.phone ?? '',
          logo_url: parsedValue?.logo_url ?? '',
        });
      }
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => {
      const companySetting = settings.find((s: any) => s.category === 'company');
      if (companySetting) {
        return settingsApi.system.update(companySetting.id, { setting_value: JSON.stringify(data) });
      }
      return settingsApi.system.create({ 
        setting_key: 'company_info', 
        setting_value: JSON.stringify(data), 
        category: 'company',
        value_type: 'json'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      toast({ title: 'Company settings saved successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Manage your company information and branding</h2>
       
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>Company Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input id="company_name" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Name</Label>
              <Input id="legal_name" value={formData.legal_name} onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <PhoneInput
                id="phone"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input id="logo_url" value={formData.logo_url} onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} rows={3} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default CompanySettings;